import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Scale, ShieldCheck, FileText, ArrowLeft } from "lucide-react";

const SECTIONS = [
  { key: "terms",    label: "Conditions Générales d’Utilisation", icon: FileText },
  { key: "privacy",  label: "Politique de confidentialité (RGPD)", icon: ShieldCheck },
  { key: "mentions", label: "Mentions légales",                    icon: Scale },
];

const UPDATED = "1er février 2026";

// Each block is rendered as plain text via JS expression — apostrophes safe.
const TERMS_BLOCKS = [
  { h: "1. Objet", p: ["XookPub est une plateforme SaaS de création publicitaire propulsée par l’intelligence artificielle. Elle permet à ses utilisateurs de générer des visuels (flyers), des vidéos courtes, des voix off et de composer des publicités complètes prêtes à diffuser."] },
  { h: "2. Acceptation", p: ["L’utilisation de XookPub implique l’acceptation pleine et entière des présentes CGU. L’utilisateur reconnaît avoir pris connaissance des présentes conditions au moment de la création de son compte."] },
  { h: "3. Comptes utilisateurs", p: ["L’utilisateur s’engage à fournir des informations exactes lors de son inscription. Il est seul responsable de la confidentialité de ses identifiants et de toute activité effectuée depuis son compte. Tout usage frauduleux entraînera la suspension immédiate du compte."] },
  { h: "4. Crédits et abonnements", p: ["L’accès aux générations IA s’effectue via un système de crédits prépayés ou via un abonnement mensuel. Les crédits achetés ne sont pas remboursables sauf disposition légale contraire. Les abonnements sont renouvelés automatiquement sauf résiliation par l’utilisateur depuis son tableau de bord."] },
  { h: "5. Propriété intellectuelle des créations", p: ["Les créations générées par XookPub (visuels, vidéos, voix) sont la propriété exclusive de l’utilisateur qui les a commandées, dès lors que celui-ci dispose des droits sur les contenus sources qu’il a fournis (logos, images, voix). XookPub se réserve un droit non-exclusif d’usage des créations à des fins de démonstration sur sa propre plateforme."] },
  { h: "6. Contenus interdits", p: ["L’utilisateur s’interdit de générer, via la plateforme, tout contenu : à caractère pornographique, violent, haineux, diffamatoire, raciste, sexiste, homophobe ; portant atteinte à la vie privée d’un tiers ; portant atteinte aux droits de propriété intellectuelle de tiers ; visant à tromper le consommateur (deepfake, désinformation)."] },
  { h: "7. Voix clonée", p: ["L’utilisateur garantit qu’il dispose du consentement explicite et écrit de toute personne dont la voix serait clonée via la plateforme. XookPub n’assume aucune responsabilité en cas de violation de ce principe."] },
  { h: "8. Disponibilité", p: ["XookPub s’engage à fournir un service disponible 24/7 avec un objectif de disponibilité de 99%. Des interruptions de maintenance peuvent intervenir et seront communiquées dans la mesure du possible."] },
  { h: "9. Limitation de responsabilité", p: ["XookPub ne saurait être tenu responsable des préjudices indirects résultant de l’utilisation de la plateforme. La responsabilité est limitée au montant payé par l’utilisateur au cours des 12 derniers mois."] },
  { h: "10. Modification des CGU", p: ["XookPub se réserve le droit de modifier les présentes CGU à tout moment. Les utilisateurs seront notifiés par email au moins 15 jours avant l’entrée en vigueur des modifications."] },
  { h: "11. Loi applicable", p: ["Les présentes CGU sont régies par le droit français. Tout litige sera porté devant les juridictions compétentes du ressort du siège social de l’éditeur."] },
];

const PRIVACY_BLOCKS = [
  { h: "1. Responsable du traitement", p: ["XookPub, éditeur de la plateforme, est responsable du traitement des données personnelles collectées sur ce site, conformément au Règlement Général sur la Protection des Données (RGPD - UE 2016/679)."] },
  {
    h: "2. Données collectées",
    list: [
      "Données d’identification : nom, email, mot de passe chiffré (bcrypt).",
      "Données de facturation : adresse, historique de paiements (traités par Stripe).",
      "Données de création : briefs textuels, fichiers logos importés, créations générées.",
      "Données techniques : adresse IP, type de navigateur, logs d’activité.",
    ],
  },
  { h: "3. Finalités du traitement", p: ["Vos données sont utilisées exclusivement pour : fournir le service (génération IA, livraison des créations) ; gérer la facturation et le support client ; améliorer la qualité du service ; respecter nos obligations légales."] },
  { h: "4. Base légale", p: ["Le traitement repose sur l’exécution du contrat de service (CGU) accepté lors de l’inscription, ainsi que sur le consentement explicite pour les communications marketing (optionnel et révocable)."] },
  {
    h: "5. Sous-traitants et transferts",
    p: ["XookPub fait appel aux sous-traitants suivants, sélectionnés pour leur conformité RGPD :"],
    list: [
      "OpenAI (États-Unis) — génération de vidéos (Sora 2) — clauses contractuelles types.",
      "Google Cloud (UE) — génération d’images (Gemini).",
      "ElevenLabs (États-Unis) — synthèse vocale — clauses contractuelles types.",
      "Stripe (Irlande) — traitement des paiements.",
      "Resend (États-Unis) — envoi d’emails transactionnels.",
      "MongoDB Atlas (UE) — hébergement de la base de données.",
    ],
  },
  { h: "6. Durée de conservation", p: ["Vos données de compte sont conservées tant que votre compte est actif. Après suppression, elles sont conservées 3 ans à des fins de prospection commerciale puis archivées 10 ans pour obligations comptables. Les créations générées sont supprimées 90 jours après leur production sauf téléchargement ou enregistrement explicite."] },
  {
    h: "7. Vos droits RGPD",
    p: ["Conformément au RGPD, vous disposez des droits suivants :"],
    list: [
      "Droit d’accès à vos données",
      "Droit de rectification",
      "Droit à l’effacement (« droit à l’oubli »)",
      "Droit à la limitation du traitement",
      "Droit à la portabilité",
      "Droit d’opposition",
      "Droit de définir des directives post-mortem",
    ],
    after: { type: "contact", text: "Pour exercer ces droits, contactez-nous à ", email: "contact@xookpub.com", suffix: ". Une réponse vous sera apportée sous 30 jours maximum." },
  },
  { h: "8. Cookies", p: ["Nous utilisons uniquement des cookies strictement nécessaires au fonctionnement du service (authentification via cookie HttpOnly sécurisé). Aucun cookie publicitaire ou de tracking tiers n’est déposé sans votre consentement."] },
  { h: "9. Sécurité", p: ["Les mots de passe sont hachés avec bcrypt. Les sessions utilisent des cookies HttpOnly Secure. Les communications sont chiffrées en HTTPS/TLS 1.3. Les paiements sont délégués à Stripe (PCI-DSS niveau 1)."] },
  { h: "10. Réclamation CNIL", p: ["Vous avez le droit d’introduire une réclamation auprès de la CNIL (Commission Nationale de l’Informatique et des Libertés) — cnil.fr."] },
];

const MENTIONS_BLOCKS = [
  {
    h: "Éditeur du site",
    p: ["XookPub — Plateforme de création publicitaire propulsée par l’IA."],
    custom: "editor",
  },
  { h: "Directeur de la publication", p: ["Le directeur de la publication est le représentant légal de XookPub."] },
  {
    h: "Hébergement",
    p: ["La plateforme XookPub est hébergée par Emergent, infrastructure cloud sécurisée Kubernetes."],
    custom: "host",
  },
  { h: "Propriété intellectuelle", p: ["L’ensemble des éléments composant la plateforme XookPub (textes, charte graphique, logos, code source) sont protégés par le droit d’auteur et appartiennent à XookPub. Toute reproduction non autorisée est strictement interdite."] },
  { h: "Crédits", p: ["Technologies utilisées : React · FastAPI · MongoDB · OpenAI Sora 2 · Google Gemini · ElevenLabs · Stripe · Resend · FFMPEG."] },
  { h: "Médiation", p: ["Conformément à l’article L.612-1 du Code de la consommation, le consommateur peut recourir gratuitement au service de médiation en cas de litige non résolu amiablement."] },
  { h: "Signalement", p: ["Pour tout signalement de contenu illicite généré via la plateforme, contactez-nous immédiatement."], custom: "abuse" },
];

function Block({ block }) {
  return (
    <>
      <h2>{block.h}</h2>
      {(block.p || []).map((para, i) => <p key={i}>{para}</p>)}
      {block.list && (
        <ul>
          {block.list.map((li, i) => <li key={i}>{li}</li>)}
        </ul>
      )}
      {block.after?.type === "contact" && (
        <p>
          {block.after.text}
          <a href={`mailto:${block.after.email}`} className="text-gold">{block.after.email}</a>
          {block.after.suffix}
        </p>
      )}
      {block.custom === "editor" && (
        <p>
          Site web : <a href="https://xookpub.com" className="text-gold">xookpub.com</a>
          <br />
          Email : <a href="mailto:contact@xookpub.com" className="text-gold">contact@xookpub.com</a>
        </p>
      )}
      {block.custom === "host" && (
        <p>
          Site web : <a href="https://emergent.sh" target="_blank" rel="noopener noreferrer" className="text-gold">emergent.sh</a>
        </p>
      )}
      {block.custom === "abuse" && (
        <p>
          Email : <a href="mailto:abuse@xookpub.com" className="text-gold">abuse@xookpub.com</a>
        </p>
      )}
    </>
  );
}

function RenderSection({ blocks }) {
  return (
    <div className="prose-legal">
      {blocks.map((b, i) => <Block key={i} block={b} />)}
    </div>
  );
}

export default function Legal() {
  const { section = "terms" } = useParams();
  if (!SECTIONS.find(s => s.key === section)) {
    return <Navigate to="/legal/terms" replace />;
  }

  const blocks =
    section === "terms"   ? TERMS_BLOCKS :
    section === "privacy" ? PRIVACY_BLOCKS :
                            MENTIONS_BLOCKS;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
      <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-sm mb-8" data-testid="legal-back-home">
        <ArrowLeft className="w-4 h-4" /> {"Retour à l’accueil"}
      </Link>

      <div className="grid md:grid-cols-[260px_1fr] gap-10">
        <aside className="md:sticky md:top-24 md:self-start">
          <div className="label-mono mb-4">Documents</div>
          <nav className="flex md:flex-col gap-2">
            {SECTIONS.map(s => {
              const active = s.key === section;
              return (
                <Link
                  key={s.key}
                  to={`/legal/${s.key}`}
                  data-testid={`legal-tab-${s.key}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm transition ${
                    active
                      ? "border-gold/60 bg-gold/10 text-gold"
                      : "border-white/10 text-white/70 hover:border-white/30 hover:text-white"
                  }`}
                >
                  <s.icon className="w-4 h-4 shrink-0" />
                  <span className="font-medium leading-tight">{s.label}</span>
                </Link>
              );
            })}
          </nav>
          <p className="text-xs text-white/40 mt-6">{`Dernière mise à jour : ${UPDATED}`}</p>
        </aside>

        <article>
          <div className="mb-8">
            <div className="label-mono text-white/40">DOCUMENT LÉGAL</div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight mt-2">
              {SECTIONS.find(s => s.key === section).label}
            </h1>
          </div>
          <div className="studio-card p-8" data-testid={`legal-content-${section}`}>
            <RenderSection blocks={blocks} />
          </div>
        </article>
      </div>
    </div>
  );
}
